/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { TopNavBar } from 'components/top_nav_bar';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaWomenDecisionParticipationEnum, CuffaWomenIncomeSourceEnum } from 'interfaces/cuffa.interface';

const incomeSourceApiKeys: { [key: string]: string } = {
  [CuffaWomenIncomeSourceEnum.PRESTACAO_SERVICO_EXTERNO]: 'prestacao_servico_externo',
  [CuffaWomenIncomeSourceEnum.ASSALARIADO_ATIVIDADE_FORA_PROPRIEDADE]: 'assalariado_atividade_fora_propriedade',
  [CuffaWomenIncomeSourceEnum.ATIVIDADE_PRODUTIVA_PROPRIEDADE]: 'atividade_produtiva_propriedade',
  [CuffaWomenIncomeSourceEnum.APOSENTADORIA]: 'aposentadoria',
  [CuffaWomenIncomeSourceEnum.PENSAO]: 'pensao',
  [CuffaWomenIncomeSourceEnum.RECURSOS_PROGRAMAS_SOCIAIS]: 'recursos_programas_sociais'
};

const reverseIncomeSourceApiKeys = Object.fromEntries(
  Object.entries(incomeSourceApiKeys).map(([key, value]) => [value, key])
);

export interface IParticipacaoFormValues {
  rendaMulher: string[];
  outrosAdicionados: string[];
  decisao: CuffaWomenDecisionParticipationEnum | '';
}

export function buildIncomePayload(values: IParticipacaoFormValues) {
  const naoSeAplicaSelecionado = values.rendaMulher.includes(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA);
  const incomeSourcesPayload: any = {
    nao_se_aplica: naoSeAplicaSelecionado,
    mulheres_tomada_decisao: values.decisao
  };

  Object.values(incomeSourceApiKeys).forEach(key => {
    incomeSourcesPayload[key] = false;
  });

  const outrasArray: { descricao: string }[] = [];

  if (!naoSeAplicaSelecionado) {
    values.rendaMulher.forEach(item => {
      const apiKey = incomeSourceApiKeys[item];
      if (apiKey) {
        incomeSourcesPayload[apiKey] = true;
      } else if (values.outrosAdicionados.includes(item)) {
        outrasArray.push({ descricao: item });
      }
    });

    incomeSourcesPayload.outras = outrasArray;
  } else {
    incomeSourcesPayload.outras = [];
  }

  return incomeSourcesPayload;
}

export function mapApiDataToState(apiData: any): IParticipacaoFormValues {
  const initial: IParticipacaoFormValues = { rendaMulher: [], outrosAdicionados: [], decisao: '' };
  if (!apiData) return initial;

  const incomeData = apiData.women_income_sources;
  if (!incomeData) return initial;

  const novasRendas: string[] = [];
  const novosOutros: string[] = [];

  if (incomeData.nao_se_aplica) {
    novasRendas.push(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA);
  } else {
    for (const backendKey in incomeData) {
      if (incomeData[backendKey] === true) {
        const sourceName = reverseIncomeSourceApiKeys[backendKey];
        if (sourceName) {
          novasRendas.push(sourceName);
        }
      }
    }
    if (incomeData.outras && Array.isArray(incomeData.outras)) {
      incomeData.outras.forEach((outro: any) => {
        const desc = typeof outro === 'string' ? outro : outro?.descricao;
        if (desc) {
          novasRendas.push(desc);
          novosOutros.push(desc);
        }
      });
    }
  }

  return { rendaMulher: novasRendas, outrosAdicionados: novosOutros, decisao: incomeData.mulheres_tomada_decisao || apiData.women_decision_participation || '' };
}

export default function RendaParticipacaoFeminina() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { salvarDadosParciais } = useCuffaStore();

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rendaMulher, setRendaMulher] = useState<string[]>([]);
  const [outrosAdicionados, setOutrosAdicionados] = useState<string[]>([]);
  const [novoOutro, setNovoOutro] = useState('');
  const [decisao, setDecisao] = useState<CuffaWomenDecisionParticipationEnum | ''>('');
  const [mostrarOutroInput, setMostrarOutroInput] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID da coleta não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }
      // Primeiro, tentar restaurar do localStorage (específico por uuid, depois global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.participacaoFeminina;
            if (!saved) continue;

            // Aceita forma salva pelo front: { fontesMulheres, outrosAdicionados, mulheresDecidem }
            if (Array.isArray(saved.fontesMulheres)) {
              setRendaMulher(saved.fontesMulheres);
            }
            if (Array.isArray(saved.outrosAdicionados)) {
              setOutrosAdicionados(saved.outrosAdicionados);
            }
            if (typeof saved.mulheresDecidem === 'string' || typeof saved.mulheresDecidem === 'boolean') {
              // Pode ser 'sim'/'nao' ou booleano
              if (typeof saved.mulheresDecidem === 'boolean') {
                setDecisao(saved.mulheresDecidem ? CuffaWomenDecisionParticipationEnum.SIM : CuffaWomenDecisionParticipationEnum.NAO);
              } else {
                setDecisao(saved.mulheresDecidem as CuffaWomenDecisionParticipationEnum);
              }
            }

            setInitialLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar participacaoFeminina:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar participacaoFeminina', e);
      }

      // Se não encontrou no storage, busca pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        const apiData = response.dados;

        const incomeData = apiData?.women_income_sources;

        if (incomeData) {
          setDecisao(incomeData.mulheres_tomada_decisao || apiData.women_decision_participation || '');

          const novasRendas: string[] = [];
          const novosOutros: string[] = [];

          if (incomeData.nao_se_aplica) {
            novasRendas.push(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA);
          } else {
            for (const backendKey in incomeData) {
              if (incomeData[backendKey] === true) {
                const sourceName = reverseIncomeSourceApiKeys[backendKey];
                if (sourceName) {
                  novasRendas.push(sourceName);
                }
              }
            }
            if (incomeData.outras && Array.isArray(incomeData.outras)) {
              incomeData.outras.forEach((outro: any) => {
                const desc = typeof outro === 'string' ? outro : outro?.descricao;
                if (desc) {
                  novasRendas.push(desc);
                  novosOutros.push(desc);
                }
              });
            }

          }

          setRendaMulher(novasRendas);
          setOutrosAdicionados(novosOutros);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        setSnackbar({ open: true, message: 'Falha ao carregar dados. Tente novamente.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [uuid]);

  const handleSelecionar = (nome: string) => {
    if (nome === CuffaWomenIncomeSourceEnum.NAO_SE_APLICA) {
      const isAlreadySelected = rendaMulher.includes(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA);
      setRendaMulher(isAlreadySelected ? [] : [CuffaWomenIncomeSourceEnum.NAO_SE_APLICA]);
      if (!isAlreadySelected) {
        setOutrosAdicionados([]);
      }
    } else {
      const jaSelecionado = rendaMulher.includes(nome);
      const atualizado = jaSelecionado
        ? rendaMulher.filter((item) => item !== nome)
        : [...rendaMulher.filter((item) => item !== CuffaWomenIncomeSourceEnum.NAO_SE_APLICA), nome];
      setRendaMulher(atualizado);

      if (jaSelecionado && outrosAdicionados.includes(nome)) {
        setOutrosAdicionados(outrosAdicionados.filter(item => item !== nome));
      }
    }
  };

  const handleAdicionarOutro = () => {
    const texto = novoOutro.trim();
    if (!texto) {
      setSnackbar({ open: true, message: 'Digite uma fonte de renda válida.', severity: 'error' });
      return;
    }
    const todas = [...Object.values(CuffaWomenIncomeSourceEnum), ...outrosAdicionados];
    const exists = todas.some(opt => opt.toLowerCase() === texto.toLowerCase());
    if (exists) {
      setSnackbar({ open: true, message: 'Esta fonte de renda já existe.', severity: 'error' });
      return;
    }
    setOutrosAdicionados([...outrosAdicionados, texto]);
    setRendaMulher([
      ...rendaMulher.filter(item => item !== CuffaWomenIncomeSourceEnum.NAO_SE_APLICA),
      texto
    ]);
    setNovoOutro('');
    setMostrarOutroInput(false);
  };


  const handleSave = async (navigateNext = false) => {
    if (!uuid) {
      setSnackbar({ open: true, message: 'Erro: UUID da coleta não encontrado.', severity: 'error' });
      return;
    }
    if (rendaMulher.length === 0) {
      setSnackbar({ open: true, message: `Selecione pelo menos uma fonte de renda ou "${CuffaWomenIncomeSourceEnum.NAO_SE_APLICA}".`, severity: 'error' });
      return;
    }
    if (!decisao) {
      setSnackbar({ open: true, message: 'Informe se as mulheres participam das tomadas de decisão.', severity: 'error' });
      return;
    }

    setSaving(true);
    const naoSeAplicaSelecionado = rendaMulher.includes(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA);

    const incomeSourcesPayload: any = {
      nao_se_aplica: naoSeAplicaSelecionado,
      mulheres_tomada_decisao: decisao
    };

    Object.values(incomeSourceApiKeys).forEach(key => {
      incomeSourcesPayload[key] = false;
    });

    const outrasArray: { descricao: string }[] = [];

    if (!naoSeAplicaSelecionado) {
      rendaMulher.forEach(item => {
        const apiKey = incomeSourceApiKeys[item];
        if (apiKey) {
          incomeSourcesPayload[apiKey] = true;
        } else if (outrosAdicionados.includes(item)) {
          outrasArray.push({ descricao: item });
        }
      });

      incomeSourcesPayload.outras = outrasArray;
    } else {
      incomeSourcesPayload.outras = [];
    }


    try {
      await CuffaAPI.updateIncomeFemaleParticipation(uuid, incomeSourcesPayload);

      salvarDadosParciais({
        participacaoFeminina: { fontesMulheres: rendaMulher, outrosAdicionados, mulheresDecidem: decisao },
        ultimaRota: location.pathname,
      });
      setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });
      if (navigateNext) {
        setTimeout(() => navigate(ROUTES.DIFICULDADES_PRODUCAO_CUFFA_INCLUIR.replace(':uuid', uuid)), 1000);
      }
    } catch (error) {
      console.error('Erro ao salvar os dados:', error);
      setSnackbar({ open: true, message: 'Falha ao salvar os dados. Tente novamente.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fontesDeRendaPredefinidas = Object.values(CuffaWomenIncomeSourceEnum).filter(
    v => v !== CuffaWomenIncomeSourceEnum.NAO_SE_APLICA && v !== CuffaWomenIncomeSourceEnum.OUTRO
  );
  const todasFontesParaExibir = [...fontesDeRendaPredefinidas, ...outrosAdicionados];

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Renda e Participação Feminina" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Renda e Participação Feminina" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Fontes de Renda Exercidas por Mulheres" width="100%">
            <Typography fontSize="14px" mb={2}>
              Assinale as fontes de renda que são exercidas por mulheres da família. Marque todas que se aplicam.
            </Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rendaMulher.includes(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA)}
                    onChange={() => handleSelecionar(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA)}
                  />
                }
                label={CuffaWomenIncomeSourceEnum.NAO_SE_APLICA}
              />
              {todasFontesParaExibir.map((nome: string) => (
                <FormControlLabel
                  key={nome}
                  control={
                    <Checkbox
                      checked={rendaMulher.includes(nome)}
                      onChange={() => handleSelecionar(nome)}
                      disabled={rendaMulher.includes(CuffaWomenIncomeSourceEnum.NAO_SE_APLICA)}
                    />
                  }
                  label={nome}
                />
              ))}
            </Box>

            {!mostrarOutroInput ? (
              <Box
                sx={{ border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem', marginTop: '1rem', cursor: 'pointer' }}
                onClick={() => setMostrarOutroInput(true)}
              >
                <AddIcon sx={{ marginRight: '0.5rem', fontSize: '1rem' }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#1a1a1a' }}>
                  Adicionar Outra Fonte de Renda
                </Typography>
              </Box>
            ) : (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  label="Outra fonte de renda"
                  value={novoOutro}
                  onChange={(e) => setNovoOutro(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAdicionarOutro();
                    }
                  }}

                />
                <IconButton color="primary" onClick={handleAdicionarOutro} disabled={!novoOutro.trim()}>
                  <AddIcon />
                </IconButton>
              </Box>
            )}

            <Box mt={4}>
              <Typography fontWeight="bold" gutterBottom>
                As mulheres participam das tomadas de decisão sobre as atividades produtivas da unidade familiar? *
              </Typography>
              <RadioGroup
                row
                value={decisao}
                onChange={(e) => setDecisao(e.target.value as CuffaWomenDecisionParticipationEnum)}
              >
                <FormControlLabel value={CuffaWomenDecisionParticipationEnum.SIM} control={<Radio />} label="Sim" />
                <FormControlLabel value={CuffaWomenDecisionParticipationEnum.NAO} control={<Radio />} label="Não" />
                <FormControlLabel value={CuffaWomenDecisionParticipationEnum.NAO_SE_APLICA} control={<Radio />} label="NÃO SE APLICA" />
              </RadioGroup>
            </Box>

            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.FONTES_RENDA_FAMILIA_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>
                Retornar
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => handleSave(false)} disabled={saving}>
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleSave(true)} disabled={saving}>
                {saving ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : null}
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}
